import { Bars3Icon, PlayIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { SolidStatusPill, getSolidStatusBgColor } from '@odo/shared-ui';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useSearchScope } from '../context/CommandPaletteContext';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useVideoProgress } from '../hooks/useVideoProgress';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import type { VideoItemData } from '../providers/videosProvider';
import { CreateResourceButton, EditResourceButton } from '../authoring/AuthoringButtons';
import InternalBadge from '../components/InternalBadge';
import PinButton from '../components/PinButton';
import RailListLayout from '../components/RailListLayout';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import VideoCard from '../components/VideoCard';
import WatchStatus from '../components/WatchStatus';
import { ResourceEmpty, ResourceError, ResourceLoading } from '../components/ResourceStates';
import { formatResourceDate } from '../utils/resourceIndex';
import { richTextToPlainText } from '../utils/richText';
import { youTubeThumbnail } from '../utils/youtube';

/** Videos with no category still need a playlist to sit in. */
const UNCATEGORISED = 'General training';

interface Playlist {
    title: string;
    videos: VideoItemData[];
}

type ViewMode = 'list' | 'grid';

const PlaylistRow: React.FC<{ video: VideoItemData; index: number; basePath: string }> = ({
    video,
    index,
    basePath
}) => {
    const { progress, percentFor } = useVideoProgress();
    const isCompleted = progress[video.slug]?.completed ?? false;
    const description = richTextToPlainText(video.description);

    return (
        <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
            <span className="text-xs font-medium text-gray-400 w-5 flex-shrink-0 tabular-nums">
                {index + 1}
            </span>
            <Link to={`${basePath}/videos/${video.slug}`} className="flex-shrink-0">
                <img
                    src={youTubeThumbnail(video.videoUrl)}
                    alt=""
                    className="w-[108px] h-[62px] rounded-lg object-cover bg-gray-100"
                />
            </Link>
            <div className="min-w-0 flex flex-col gap-1.5 flex-1">
                <Link
                    to={`${basePath}/videos/${video.slug}`}
                    className="text-sm font-bold text-gray-800 hover:underline truncate"
                >
                    {video.title}
                </Link>
                {description && (
                    <span className="text-xs text-gray-500 truncate">{description}</span>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                    <InternalBadge audience={video.audience} />
                    {video.category.slice(0, 2).map((cat) => (
                        <SolidStatusPill key={cat} value={cat} />
                    ))}
                    <span className="text-xs text-gray-400">
                        Released {formatResourceDate(video.createdAt)}
                    </span>
                </div>
            </div>
            <WatchStatus
                completed={isCompleted}
                percent={percentFor(video.slug)}
                barClassName="w-20"
                className="flex-shrink-0"
            />
            <EditResourceButton
                kind="video"
                item={video}
                name={video.title}
                className="flex-shrink-0"
            />
            <PinButton kind="video" id={video.id} title={video.title} className="flex-shrink-0" />
        </div>
    );
};

const PlaylistSection: React.FC<{ playlist: Playlist; basePath: string }> = ({
    playlist,
    basePath
}) => {
    const { progress } = useVideoProgress();
    const completed = playlist.videos.filter((video) => progress[video.slug]?.completed).length;
    // Resume points at the first unfinished video, else the first.
    const resumeTarget =
        playlist.videos.find((video) => !progress[video.slug]?.completed) ?? playlist.videos[0];

    return (
        <section className="flex flex-col gap-3.5">
            <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-base font-bold text-gray-800">{playlist.title}</h2>
                <span className="text-sm text-gray-500">
                    {playlist.videos.length} {playlist.videos.length === 1 ? 'video' : 'videos'} ·{' '}
                    {completed} completed
                </span>
                <Link
                    to={`${basePath}/videos/${resumeTarget.slug}`}
                    className="ml-auto h-8 inline-flex items-center gap-1.5 px-3.5 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <PlayIcon className="w-3.5 h-3.5" />
                    {completed > 0 ? 'Resume playlist' : 'Start playlist'}
                </Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {playlist.videos.map((video, index) => (
                    <PlaylistRow key={video.id} video={video} index={index} basePath={basePath} />
                ))}
            </div>
        </section>
    );
};

/** Videos as per-category playlists; progress is browser-local (useVideoProgress). */
export const VideosPage: React.FC = () => {
    const { basePath } = useResourcesConfig();
    const { videos, isLoading, error, refresh } = useResourcesLibrary();
    const [view, setView] = useState<ViewMode>('list');
    const [category, setCategory] = useState<string | null>(null);

    useResourcePageTitle('Videos');
    useSearchScope('video');

    const playlists = useMemo<Playlist[]>(() => {
        const byCategory = new Map<string, VideoItemData[]>();
        const chronological = [...videos].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        for (const video of chronological) {
            const categories = video.category.length > 0 ? video.category : [UNCATEGORISED];
            for (const category of categories) {
                const bucket = byCategory.get(category);
                if (bucket) bucket.push(video);
                else byCategory.set(category, [video]);
            }
        }
        return Array.from(byCategory.entries())
            .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
            .map(([title, items]) => ({ title, videos: items }));
    }, [videos]);

    /** The rail filters both views: playlists in list view, cards in grid view. */
    const visiblePlaylists = useMemo(
        () => (category ? playlists.filter((list) => list.title === category) : playlists),
        [playlists, category]
    );

    const newestFirst = useMemo(
        () =>
            [...videos]
                .filter(
                    (video) =>
                        !category ||
                        (video.category.length > 0 ? video.category : [UNCATEGORISED]).includes(
                            category
                        )
                )
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        [videos, category]
    );

    const railItems = useMemo(
        () => [
            {
                key: 'all',
                label: 'All',
                count: videos.length,
                active: category === null,
                onSelect: () => setCategory(null)
            },
            ...playlists.map((list) => ({
                key: list.title,
                label: list.title,
                count: list.videos.length,
                active: category === list.title,
                onSelect: () => setCategory(list.title),
                dotClassName: getSolidStatusBgColor(list.title)
            }))
        ],
        [playlists, videos.length, category]
    );

    const body = () => {
        if (isLoading && videos.length === 0) return <ResourceLoading label="Loading videos…" />;
        if (error && videos.length === 0) {
            return <ResourceError message={error} onRetry={() => refresh(true)} />;
        }
        if (videos.length === 0) {
            return (
                <ResourceEmpty
                    title="No videos yet"
                    description="There are no training videos available at the moment. Check back later."
                />
            );
        }

        return (
            <RailListLayout
                railItems={railItems}
                innerClassName={
                    view === 'grid'
                        ? 'grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6'
                        : 'flex flex-col gap-7'
                }
            >
                {view === 'grid'
                    ? newestFirst.map((video) => <VideoCard key={video.id} {...video} />)
                    : visiblePlaylists.map((playlist) => (
                          <PlaylistSection
                              key={playlist.title}
                              playlist={playlist}
                              basePath={basePath}
                          />
                      ))}
            </RailListLayout>
        );
    };

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <ResourcesPageHeader
                title="Videos"
                searchPlaceholder="Search videos…"
                actions={
                    <>
                        <CreateResourceButton kind="video" />
                        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setView('list')}
                                aria-pressed={view === 'list'}
                                aria-label="Playlist view"
                                className={`p-2.5 transition-colors ${
                                    view === 'list'
                                        ? 'bg-gray-800 text-white'
                                        : 'text-gray-400 hover:bg-gray-50'
                                }`}
                            >
                                <Bars3Icon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setView('grid')}
                                aria-pressed={view === 'grid'}
                                aria-label="Grid view"
                                className={`p-2.5 transition-colors ${
                                    view === 'grid'
                                        ? 'bg-gray-800 text-white'
                                        : 'text-gray-400 hover:bg-gray-50'
                                }`}
                            >
                                <Squares2X2Icon className="w-4 h-4" />
                            </button>
                        </div>
                    </>
                }
            />
            <div className="flex-1 min-h-0 px-8 pt-6 pb-8">{body()}</div>
        </div>
    );
};

export default VideosPage;
